import { useTheme } from '@mui/material'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetProjectMapSVG } from 'src/api/project'
import { CenteredSpinner } from 'src/components/Spinner'
import { IPosition, IPositionType } from 'src/generated-types'
import {
  ConfigurationCard,
  ConfigureView,
  PositionConfig,
  QuickSelectMode,
  ZoomRequest,
} from 'src/generated-types/configure-positions'
import { DrawingColumn, DrawingViewport, SvgHost } from './ConfigurePositionsDrawing.style'
import { getConfigurePalette } from './configurePalette'
import { isUngroupedName } from './configurePositions.action'
import { positionLabel } from './positionLabels'
import DrawingHints from './DrawingHints'
import DrawingToolbar from './DrawingToolbar'
import DrawingTooltip, { DrawingTooltipHandle } from './DrawingTooltip'
import PositionListView from './PositionListView'
import { setHoveredPosition } from './drawingSvgElements'
import { useDrawingSelection } from './useDrawingSelection'
import { useDrawingPanZoom } from './useDrawingPanZoom'
import { useDrawingSvgPaint } from './useDrawingSvgPaint'
import { Translate, changeSummaryLines } from '../../../versions/projectVersionChanges.action'
import RevisionToolbar from '../../../versions/RevisionToolbar'
import { RevisionConfigure } from '../../../versions/useRevisionConfigure'
import { useRevisionSvgPaint } from '../../../versions/useRevisionSvgPaint'
import { useRevisionMapClick } from '../../../versions/useRevisionMapActions'
import RevisionViewNote from '../../../versions/RevisionViewNote'

interface ConfigurePositionsDrawingProps {
  customerId: EntityId
  localityId: EntityId
  projectId: EntityId
  view: ConfigureView
  zoom: number
  onView: (view: ConfigureView) => void
  onZoom: (request: ZoomRequest) => void
  activeType: IPositionType
  typeLabel: string
  positions: IPosition[]
  allPositions: IPosition[]
  cages: IPosition[]
  configById: Map<number, PositionConfig>
  cards: ConfigurationCard[]
  highlightedIds: Set<number>
  onHoverPosition: (id: number | null) => void
  selectedIds: Set<number>
  onPositionClick: (id: number) => void
  onAdd: (ids: number[]) => void
  onRemove: (ids: number[]) => void
  onToggleGroup: (ids: number[]) => void
  onQuickSelect: (mode: QuickSelectMode) => void
  revision?: RevisionConfigure
}

const ConfigurePositionsDrawing: React.FC<ConfigurePositionsDrawingProps> = ({
  customerId,
  localityId,
  projectId,
  view,
  zoom,
  onView,
  onZoom,
  activeType,
  typeLabel,
  positions,
  allPositions,
  cages,
  configById,
  cards,
  highlightedIds,
  onHoverPosition,
  selectedIds,
  onPositionClick,
  onAdd,
  onRemove,
  onToggleGroup,
  onQuickSelect,
  revision,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['position', 'component', 'projects'])
  const palette = useMemo(() => getConfigurePalette(theme), [theme])
  const { data: currentSvg, isLoading } = useGetProjectMapSVG(
    customerId,
    localityId,
    projectId,
    !!customerId && !!localityId && !!projectId,
  )
  // The published map has no ids of this draft, so it is shown as it was, without selection.
  const svg = revision?.view === 'before' ? revision.baseSvg : currentSvg
  const onRevisionClick = useRevisionMapClick({
    revision,
    customerId,
    localityId,
    projectId,
    allPositions,
    onLineAdded: id => {
      onQuickSelect('none')
      onAdd([id])
    },
  })
  const columnRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<DrawingTooltipHandle>(null)
  const isDrawingView = view === 'drawing'

  const svgPaint = useDrawingSvgPaint({
    hostRef,
    svg,
    positions: allPositions,
    cages,
    activeType,
    selectedIds,
    configById,
    highlightedIds,
    palette,
    // The revision state is memoised, so a new object means what is drawn over the map changed.
    repaintKey: revision,
  })
  const changedIds = useMemo(
    () =>
      revision
        ? allPositions.filter(p => p.type === activeType && revision.changeByName.has(p.name ?? '')).map(p => p.id)
        : [],
    [revision, allPositions, activeType],
  )
  useRevisionSvgPaint({
    hostRef,
    svg,
    positions: allPositions,
    activeType,
    selectedIds,
    palette,
    theme,
    revision,
    highlightedIds,
    configById,
    repaintTriggers: [cages],
  })
  const panZoom = useDrawingPanZoom({ view, zoom, onZoom, hostRef, viewportRef })
  const selection = useDrawingSelection({
    hostRef,
    positionShapesRef: svgPaint.positionShapesRef,
    cageShapesRef: svgPaint.cageShapesRef,
    marqueeRef: svgPaint.marqueeRef,
    activeType,
    typeLabel,
    palette,
    onAdd,
    onRemove,
    onToggleGroup,
  })

  const setHover = (positionId: string | null) => {
    const host = hostRef.current
    if (!host || svgPaint.hoveredIdRef.current === positionId) return
    svgPaint.hoveredIdRef.current = positionId
    setHoveredPosition(host, positionId)
    onHoverPosition(positionId ? Number(positionId) : null)
  }

  const clearHover = () => {
    setHover(null)
    tooltipRef.current?.hide()
  }

  const showPositionTooltip = (event: React.PointerEvent, positionId: number) => {
    const position = allPositions.find(item => item.id === positionId)
    const config = configById.get(positionId)
    const details = [
      config?.drawingNumber
        ? `${t('drawingNo', { ns: 'component' })}: ${config.drawingNumber}`
        : t('no-drawing', { ns: 'position' }),
    ]
    if (config?.groupName && !isUngroupedName(config.groupName)) {
      details.push(`${t('group', { ns: 'position' })}: ${config.groupName}`)
    }
    const change = revision?.highlight ? revision.changeByName.get(position?.name ?? '') : undefined
    if (change) {
      const projects: Translate = (key, options) => t(key, { ns: 'projects', ...options }) as string
      details.push(...changeSummaryLines(change, projects))
    } else if (revision?.highlight) details.push(t('revision-unchanged', { ns: 'projects' }))
    tooltipRef.current?.show(event, position ? positionLabel(position) : String(positionId), details.join('\n'))
  }

  const onPointerDown = (event: React.PointerEvent) => {
    if (!isDrawingView) return
    if (panZoom.startPan(event)) {
      tooltipRef.current?.hide()
      return
    }
    if (selection.startMarquee(event)) clearHover()
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (panZoom.movePan(event) || selection.moveMarquee(event) || !isDrawingView) return
    const target = (event.target as Element).closest?.('[data-cfg-id]')
    const positionType = Number(target?.getAttribute('data-cfg-type'))
    if (!target || positionType === IPositionType.Cage) {
      clearHover()
      return
    }
    const positionId = Number(target.getAttribute('data-cfg-id'))
    setHover(positionType === activeType ? String(positionId) : null)
    showPositionTooltip(event, positionId)
  }

  const onPointerUp = (event: React.PointerEvent) => {
    const pan = panZoom.endPan()
    if (pan) {
      if (pan.moved) selection.suppressNextClick()
      return
    }
    selection.endMarquee(event)
  }

  const onClick = (event: React.MouseEvent) => {
    if (panZoom.spaceHeldRef.current) return
    onRevisionClick(event, () => selection.resolveClick(event), onPositionClick)
  }

  const preventInDrawing = (event: React.SyntheticEvent) => {
    if (isDrawingView) event.preventDefault()
  }

  const cursorMode = !isDrawingView
    ? 'default'
    : panZoom.isPanning
    ? 'grabbing'
    : panZoom.isHandMode
    ? 'grab'
    : 'default'
  const toolCursor =
    revision?.view === 'after'
      ? { addLine: 'copy', remove: 'not-allowed', select: undefined }[revision.tool]
      : undefined

  return (
    <DrawingColumn ref={columnRef}>
      <DrawingToolbar
        view={view}
        zoom={zoom}
        palette={palette}
        onView={onView}
        onQuickSelect={mode => {
          if (mode === 'none') revision?.setSelectedRemoved([])
          onQuickSelect(mode)
        }}
        onZoom={panZoom.zoomFromCenter}
        onSelectChanged={revision ? () => onAdd(changedIds) : undefined}
      />
      {revision && <RevisionToolbar revision={revision} />}
      <DrawingViewport
        ref={viewportRef}
        cursorMode={cursorMode}
        style={toolCursor ? { cursor: toolCursor } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          panZoom.endPan()
          selection.cancelMarquee()
        }}
        onPointerEnter={panZoom.onPointerEnter}
        onPointerLeave={() => {
          panZoom.onPointerLeave()
          clearHover()
        }}
        onClick={onClick}
        onMouseDown={event => {
          if (event.button === 1 || event.button === 2) preventInDrawing(event)
        }}
        onContextMenu={preventInDrawing}
        onAuxClick={preventInDrawing}
      >
        {isLoading && <CenteredSpinner />}
        {revision && isDrawingView && <RevisionViewNote revision={revision} />}
        <SvgHost ref={hostRef} zoom={zoom} visible={isDrawingView} dangerouslySetInnerHTML={{ __html: svg || '' }} />
        {view === 'list' && (
          <PositionListView
            positions={positions}
            cards={cards}
            configById={configById}
            selectedIds={selectedIds}
            onPositionClick={onPositionClick}
            onAdd={onAdd}
            revision={revision}
            activeType={activeType}
          />
        )}
      </DrawingViewport>
      <DrawingHints view={view} activeType={activeType} palette={palette} revision={revision} />
      <DrawingTooltip ref={tooltipRef} containerRef={columnRef} />
    </DrawingColumn>
  )
}

export default ConfigurePositionsDrawing
